import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus, Save } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const REVIEW_STATUSES = [
  { value: 'clear', label: 'Clear' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'false_positive', label: 'False Positive' },
];

const SEVERITIES = [
  { value: '', label: 'None' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const ISSUE_TAGS = [
  'gender',
  'pronouns',
  'name_spelling',
  'age',
  'relationship',
  'logic',
  'cipher_hint',
  'cipher_unlock',
  'cipher_reward',
  'image_prompt',
  'r2_storage',
  'runtime_gm',
  'runtime_identity',
  'guide_identity',
  'stale_guide_name',
  'news',
  'user_report',
];

const selectClassName = 'h-9 rounded-md border border-gray-700 bg-gray-950 px-3 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-amber-500';
const textareaClassName = 'min-h-[96px] w-full rounded-md border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-amber-500';

const statusTone = (status) => {
  switch (status) {
    case 'needs_review':
      return 'bg-amber-900/50 text-amber-300 border border-amber-700';
    case 'investigating':
      return 'bg-sky-900/50 text-sky-300 border border-sky-700';
    case 'resolved':
      return 'bg-emerald-900/50 text-emerald-300 border border-emerald-700';
    case 'false_positive':
      return 'bg-purple-900/50 text-purple-300 border border-purple-700';
    default:
      return 'bg-gray-800 text-gray-300 border border-gray-700';
  }
};

const severityTone = (severity) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-900/70 text-red-200 border border-red-600';
    case 'high':
      return 'bg-red-900/50 text-red-300 border border-red-700';
    case 'medium':
      return 'bg-orange-900/50 text-orange-300 border border-orange-700';
    case 'low':
      return 'bg-blue-900/50 text-blue-300 border border-blue-700';
    default:
      return 'bg-gray-800 text-gray-400 border border-gray-700';
  }
};

const formatLabel = (value) => value.replace(/_/g, ' ');

const formatDateTime = (dateString) => {
  if (!dateString) return 'Unknown';
  let normalizedDate = dateString;
  if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
    normalizedDate = `${dateString}Z`;
  }
  return new Date(normalizedDate).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const ReviewStatusPanel = ({
  caseId,
  review,
  loading = false,
  disabled = false,
  onSaveReview,
  onAddNote,
}) => {
  const [status, setStatus] = useState('clear');
  const [severity, setSeverity] = useState('');
  const [summary, setSummary] = useState('');
  const [issueTags, setIssueTags] = useState([]);
  const [noteBody, setNoteBody] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const currentCaseIdRef = useRef(caseId);
  const saveRequestIdRef = useRef(0);
  const noteRequestIdRef = useRef(0);

  currentCaseIdRef.current = caseId;

  useEffect(() => {
    setStatus(review?.status || 'clear');
    setSeverity(review?.severity || '');
    setSummary(review?.summary || '');
    setIssueTags(Array.isArray(review?.issue_tags) ? review.issue_tags : []);
  }, [review]);

  useEffect(() => {
    setNoteBody('');
  }, [caseId]);

  const selectedTagSet = useMemo(() => new Set(issueTags), [issueTags]);
  const notes = Array.isArray(review?.notes) ? review.notes.filter((note) => !note.deleted) : [];
  const statusLabel = REVIEW_STATUSES.find((option) => option.value === status)?.label || formatLabel(status);

  const toggleTag = (tag) => {
    setIssueTags((currentTags) => (
      currentTags.includes(tag)
        ? currentTags.filter((currentTag) => currentTag !== tag)
        : [...currentTags, tag]
    ));
  };

  const handleSaveReview = async () => {
    if (disabled) return;

    setSavingReview(true);
    const requestId = saveRequestIdRef.current + 1;
    saveRequestIdRef.current = requestId;
    try {
      await onSaveReview?.({
        status,
        severity: severity || null,
        issue_tags: issueTags,
        summary,
      });
    } finally {
      if (saveRequestIdRef.current === requestId) {
        setSavingReview(false);
      }
    }
  };

  const handleAddNote = async () => {
    const body = noteBody.trim();
    if (!body || disabled) return;

    setSavingNote(true);
    const requestId = noteRequestIdRef.current + 1;
    noteRequestIdRef.current = requestId;
    const requestCaseId = caseId;
    try {
      await onAddNote?.({
        body,
        severity: severity || null,
        issue_tags: issueTags,
      });
      if (noteRequestIdRef.current === requestId && currentCaseIdRef.current === requestCaseId) {
        setNoteBody((currentBody) => (currentBody.trim() === body ? '' : currentBody));
      }
    } finally {
      if (noteRequestIdRef.current === requestId) {
        setSavingNote(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm">Loading review metadata...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-lg border border-gray-700 bg-gray-900/60 p-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Case Review</h2>
          <p className="text-sm text-gray-400">Private admin status, issue tags, and notes for this case.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className={statusTone(status)}>{statusLabel}</Badge>
          <Badge className={severityTone(severity)}>{severity || 'no severity'}</Badge>
        </div>
      </div>

      <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            Status
            <select className={selectClassName} value={status} onChange={(event) => setStatus(event.target.value)} disabled={disabled}>
              {REVIEW_STATUSES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            Severity
            <select className={selectClassName} value={severity} onChange={(event) => setSeverity(event.target.value)} disabled={disabled}>
              {SEVERITIES.map((option) => (
                <option key={option.value || 'none'} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="mt-4 flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
          Summary
          <textarea
            className={textareaClassName}
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            maxLength={500}
            placeholder="Short review summary"
            disabled={disabled}
          />
        </label>

        <div className="mt-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Issue Tags</div>
          <div className="flex flex-wrap gap-2">
            {ISSUE_TAGS.map((tag) => {
              const selected = selectedTagSet.has(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  disabled={disabled}
                  className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    selected
                      ? 'border-amber-600 bg-amber-900/30 text-amber-200'
                      : 'border-gray-700 bg-gray-950 text-gray-400 hover:border-gray-600 hover:text-gray-200'
                  }`}
                  aria-pressed={selected}
                >
                  {formatLabel(tag)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            onClick={handleSaveReview}
            disabled={savingReview || loading || disabled}
            className="bg-amber-600 text-white hover:bg-amber-500"
          >
            {savingReview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Review
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white">Admin-only Notes</h3>
            <p className="text-sm text-gray-400">Notes are private to admins.</p>
          </div>
          <Badge variant="outline" className="border-gray-700 text-gray-400">
            {notes.length}
          </Badge>
        </div>

        <textarea
          className={textareaClassName}
          value={noteBody}
          onChange={(event) => setNoteBody(event.target.value)}
          maxLength={2000}
          placeholder="Add a private note"
          disabled={disabled}
        />
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleAddNote}
            disabled={savingNote || disabled || !noteBody.trim()}
            className="border-gray-700 text-gray-200"
          >
            {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add Note
          </Button>
        </div>

        <div className="mt-4 space-y-3">
          {notes.length === 0 ? (
            <div className="rounded-md border border-gray-800 bg-gray-950/60 p-4 text-center text-sm text-gray-500">
              No admin notes yet.
            </div>
          ) : notes.map((note) => (
            <div key={note.id || `${note.created_at}-${note.body}`} className="rounded-md border border-gray-800 bg-gray-950/60 p-3">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">{formatDateTime(note.created_at)}</span>
                {note.severity && (
                  <Badge className={`${severityTone(note.severity)} text-xs`}>{note.severity}</Badge>
                )}
                {(note.issue_tags || []).map((tag) => (
                  <Badge key={tag} variant="outline" className="border-gray-700 text-gray-400 text-xs">
                    {formatLabel(tag)}
                  </Badge>
                ))}
              </div>
              <p className="whitespace-pre-wrap text-sm text-gray-300">{note.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewStatusPanel;
